import { Avatar } from '@mantine/core';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign, TTDCampaign } from '@feathr/blackbox';
import { EIntegrationTypes, isTTDCampaignWithObjective } from '@feathr/blackbox';
import { Text, Tooltip } from '@feathr/components';
import { CampaignStatusChip } from '@feathr/extender/components';
import type { IObjectiveOptionWithIcon } from '@feathr/extender/hooks';
import { useObjectiveUtils } from '@feathr/extender/hooks';
import { useFlags, useStore } from '@feathr/extender/state';
import { getIconForAction } from '@feathr/hooks';

import CampaignTypeChip from '../CampaignTypeChip';

import styles from './CampaignChips.css';

interface ICampaignChipsProps {
  campaign: Campaign;
  context?: 'table' | 'header';
}

function CampaignChips({
  campaign,
  context = 'header',
}: Readonly<ICampaignChipsProps>): JSX.Element {
  const { t } = useTranslation();
  const { Targetings } = useStore();
  const { createSelectOptions } = useObjectiveUtils();
  const { metaV2: hasMetaV2Flag } = useFlags();

  const cls = campaign.get('_cls');

  let objectives: IObjectiveOptionWithIcon[] = [];
  if (isTTDCampaignWithObjective(cls)) {
    const TTDCampaign = campaign as TTDCampaign;
    objectives = createSelectOptions(TTDCampaign.getAvailableObjectives());
  }
  const objective = objectives.find((objective) => objective.id === campaign.get('objective'));

  // Filter from local collection to include ephemeral targetings
  const lookalikeTargetings = Targetings.models.filter(
    (t) =>
      t.get('parent') === campaign.id &&
      !t.get('is_archived') &&
      t.get('kind') === 'omni_lookalike',
  );

  const integrations = campaign.get('integrations');
  const hasNoIntegrations = !integrations || integrations.length === 0;
  const isTTDCampaign = isTTDCampaignWithObjective(cls);
  const hasDisplay =
    isTTDCampaign && (hasNoIntegrations || integrations?.includes(EIntegrationTypes.Tradedesk));
  const hasMeta = hasMetaV2Flag && integrations?.includes(EIntegrationTypes.Facebook);

  const hasCTV = false;
  const hasLookalike = lookalikeTargetings.length > 0;
  // We will only show a maximum of 4 icons here. If there are 5 or more we will show the
  // first 3 and then a +2 icon
  const hasAdditionalTargets = false;

  const hasTargets = hasCTV || hasMeta || hasLookalike || (hasMetaV2Flag && hasDisplay);

  return (
    <div className={classNames(styles.root, { [styles.table]: context === 'table' })}>
      <div className={styles.chips}>
        <CampaignTypeChip cls={cls} isMonetization={campaign.isMonetization} />
        <CampaignStatusChip campaign={campaign} />
      </div>
      {objective && (
        <Text.DefaultRegular className={styles.objective}>{objective.name}</Text.DefaultRegular>
      )}
      {hasTargets && (
        <Avatar.Group className={styles.avatarGroup} spacing={'6'}>
          {hasMeta && (
            <Tooltip title={t('Meta')}>
              <Avatar alt={t('Meta')} color={'gray.1'} size={'24'} variant={'filled'}>
                <span className={styles.metaIcon}>{getIconForAction('meta')}</span>
              </Avatar>
            </Tooltip>
          )}
          {hasMetaV2Flag && hasDisplay && (
            <Tooltip title={t('Display Advertising')}>
              <Avatar
                alt={t('Display Advertising')}
                color={'cyan.6'}
                size={'24'}
                variant={'filled'}
              >
                {getIconForAction('displayAd')}
              </Avatar>
            </Tooltip>
          )}
          {hasCTV && (
            <Tooltip title={t('CTV')}>
              <Avatar alt={t('CTV')} color={'cyan.6'} size={'24'} variant={'filled'}>
                {getIconForAction('tv')}
              </Avatar>
            </Tooltip>
          )}
          {hasLookalike && (
            <Tooltip title={t('Lookalike')}>
              <Avatar alt={t('Lookalike')} color={'yellow.6'} size={'24'} variant={'filled'}>
                {getIconForAction('lookalike')}
              </Avatar>
            </Tooltip>
          )}
          {/* When there are more than 3 targets */}
          {hasAdditionalTargets && (
            // Not translating since this is placeholder text for additional targets since we don't know what they are yet
            <Tooltip title={'Additional target 1, Additional target 2'}>
              <Avatar color={'neutral.4'} size={'24'} variant={'filled'}>
                +2
              </Avatar>
            </Tooltip>
          )}
        </Avatar.Group>
      )}
    </div>
  );
}

export default observer(CampaignChips);
